import { Group, type InterleavedBufferAttribute } from "three";
import { Line2 } from "three/addons/lines/Line2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";

// Screen-space strokes stay legible regardless of zoom or distance to the camera.
// Only the current segment endpoint changes; completed geometry is reused.
export class ReplayTrail {
  readonly geometry = new LineGeometry();
  readonly coreMaterial = new LineMaterial({ color: 0xffad42, linewidth: 5, depthTest: false, depthWrite: false });
  readonly outlineMaterial = new LineMaterial({ color: 0x30251d, linewidth: 8, depthTest: false, depthWrite: false });
  readonly object = new Group();
  private previousSegment = 0;
  private points: [number, number, number][];

  constructor(points: [number, number, number][], color: string | number = 0xffad42) {
    this.coreMaterial.color.set(color);
    this.points = points;
    const safe = points.length > 1 ? points : [points[0] ?? [0, 0, 0], points[0] ?? [0, 0, 0]];
    this.geometry.setPositions(safe.flat());
    this.geometry.instanceCount = 0;
    const outline = new Line2(this.geometry, this.outlineMaterial);
    const core = new Line2(this.geometry, this.coreMaterial);
    outline.renderOrder = 9;
    core.renderOrder = 10;
    outline.frustumCulled = core.frustumCulled = false;
    // MapLibre owns the shared viewport; keep our explicit CSS-pixel resolution.
    outline.onBeforeRender = core.onBeforeRender = () => {};
    this.object.add(outline, core);
  }

  update(segment: number, point: [number, number, number]) {
    if (this.points.length < 2) return;
    const ends = this.geometry.attributes.instanceEnd as InterleavedBufferAttribute;
    const original = this.points[this.previousSegment + 1];
    ends.setXYZ(this.previousSegment, ...original);
    ends.setXYZ(segment, ...point);
    const buffer = ends.data;
    buffer.clearUpdateRanges();
    buffer.addUpdateRange(this.previousSegment * 6 + 3, 3);
    buffer.addUpdateRange(segment * 6 + 3, 3);
    ends.needsUpdate = true;
    this.geometry.instanceCount = segment + 1;
    this.previousSegment = segment;
  }

  resize(width: number, height: number) {
    this.coreMaterial.resolution.set(width, height);
    this.outlineMaterial.resolution.set(width, height);
  }

  dispose() {
    this.geometry.dispose();
    this.coreMaterial.dispose();
    this.outlineMaterial.dispose();
  }
}
